import { FC } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppSelector } from 'hooks';
import { selectCurrentTeam, selectIsOwner } from 'containers/app/appSlice';
import { SubscriptionStatus } from 'containers/app/app.model';
import { GreyStrokeInfoIcon } from 'assets/icons';
import routesPath from 'routes/RoutesPath';
import GlobalBanner from './components/GlobalBanner';
import useFSMPlanInfo from 'containers/integrations/fsm-integration/hooks/useFSMPlanInfo';
import { FSM_BANNER_ROUTES, FSM_FREE_PLAN_ROUTES, FSM_LITE_ENDED_MODAL_ROUTES } from './constants';

/**
 * GlobalBannerRenderer - Displays contextual banners based on subscription states and user conditions
 *
 * Usage: Place this component at the top of your page layouts to show relevant subscription
 * banners like subscription ended, payment failed, trial ending, etc.
 *
 * Similar to GlobalModalRenderer but for banner notifications instead of modals.
 *
 * @param isFullScreenLayout - Set to true to hide banners on full-screen layouts
 */
interface GlobalBannerRendererProps {
  isFullScreenLayout?: boolean;
}

const GlobalBannerRenderer: FC<GlobalBannerRendererProps> = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const currentTeam = useAppSelector(selectCurrentTeam);
  const isOwner = useAppSelector(selectIsOwner);

  const {
    isInTrial,
    hasPaymentFailed,
    isPaymentMethodUpdated,
    fsmPlanValidUntilDate,
    hasDowngradedAfterTrial,
    isUserCancelled,
    isFSMLitePlan,
    isFSMPlusPlan,
    isNoPlan,
    isInFSMGracePeriod,
    migratedTeamWithJobs
  } = useFSMPlanInfo();

  // Estimates subscription ended banner
  const showEstimateSubscriptionEndedBanner =
    isOwner &&
    currentTeam?.subscriptionStatus === SubscriptionStatus.SUBSCRIPTION_EXPIRED &&
    location.pathname === routesPath.ESTIMATES;

  const showFSMBanners =
    isOwner &&
    (FSM_BANNER_ROUTES.includes(location.pathname) || location.pathname.includes(routesPath.ACCOUNTING_PAY_RUNS));

  const isPaidPlan = isFSMLitePlan || isFSMPlusPlan;

  const getPlanName = (): string => {
    if (isFSMLitePlan) return 'Field Serve Lite';
    if (isFSMPlusPlan) return 'Field Serve Plus';
    return 'Field Serve';
  };

  const goToFSMBilling = () => {
    navigate(routesPath.FIELD_SERVICE_MANAGEMENT);
  };

  const isOnFSMLitePlanRoute =
    FSM_FREE_PLAN_ROUTES.some(route => location.pathname.includes(route)) ||
    FSM_LITE_ENDED_MODAL_ROUTES.some(route => location.pathname.includes(route));

  const showBannerForMigratedTeamsWithNoPlan =
    isOwner && (isInFSMGracePeriod || migratedTeamWithJobs) && isOnFSMLitePlanRoute && isNoPlan;

  if (showEstimateSubscriptionEndedBanner) {
    return (
      <GlobalBanner
        icon={<GreyStrokeInfoIcon />}
        message="Your estimates subscription has ended"
        actionText="Manage subscription"
        onActionClick={() => navigate(routesPath.ESTIMATE_INTEGRATION)}
      />
    );
  }

  if (showBannerForMigratedTeamsWithNoPlan) {
    return (
      <GlobalBanner
        icon={<GreyStrokeInfoIcon />}
        message="Subscribe to continue using Lite features"
        actionText="Subscribe"
        onActionClick={goToFSMBilling}
      />
    );
  }

  if (showFSMBanners && isPaidPlan) {
    const planName = getPlanName();
    const isLitePlan = isFSMLitePlan;
    if (hasDowngradedAfterTrial) {
      return (
        <GlobalBanner
          icon={<GreyStrokeInfoIcon />}
          message={`Your ${planName} trial has ended.`}
          subMessage="Manage your plan to continue using your features."
          actionText="Manage"
          onActionClick={goToFSMBilling}
        />
      );
    }
    if (isUserCancelled) {
      return (
        <GlobalBanner
          icon={<GreyStrokeInfoIcon />}
          message={`Your ${planName} subscription is canceled.`}
          subMessage={`You'll keep ${isLitePlan ? 'Lite' : 'Plus'} features until ${fsmPlanValidUntilDate}.`}
          actionText="Upgrade"
          onActionClick={goToFSMBilling}
        />
      );
    }
    if (isInTrial && !isPaymentMethodUpdated) {
      return (
        <GlobalBanner
          icon={<GreyStrokeInfoIcon />}
          message={`Your ${planName} trial is active.`}
          subMessage={`To keep ${isLitePlan ? 'Lite' : 'Plus'} features after your trial ends, 
            please add your billing info.`}
          actionText="Add billing info"
          onActionClick={goToFSMBilling}
        />
      );
    }
    if (hasPaymentFailed) {
      return (
        <GlobalBanner
          icon={<GreyStrokeInfoIcon />}
          message={`We couldn't process your ${planName} payment.`}
          subMessage={`Update billing by ${fsmPlanValidUntilDate} to avoid losing 
            ${isLitePlan ? 'Lite' : 'Plus'} features.`}
          actionText="Update billing info"
          onActionClick={goToFSMBilling}
        />
      );
    }
  }

  return null;
};

export default GlobalBannerRenderer;
